<template>
  <div class="email-details">
    <div class="email-body">
      <h1 class="title-screen">{{ adminText('organizationAll.email_details_f9c3a37c', 'Email details') }}</h1>
      <div v-if="loadError" role="alert" class="delivery-message">
        {{ adminText('organizationAll.could_not_load_email_details_please_try_again_8d151b4a', 'Could not load email details. Please try again.') }}
        <main-button flat :disabled="loading" @click="fetchEmail">{{ adminText('organizationAll.retry_0c4430e6', 'RETRY') }}</main-button>
      </div>
      <div v-else-if="loading" role="status" class="delivery-message">{{ adminText('organizationAll.loading_email_details_40049a1e', 'Loading email details…') }}</div>
      <template v-else-if="email.id">
        <h2 class="email-subject">{{ email.subject }}</h2>
        <!-- Content is organizer-authored HTML, already escaped per-recipient on send. -->
        <div class="email-content" v-html="email.content" />

        <div class="actions">
          <main-button v-if="canRetry" :loading="retrying" :disabled="retrying" @click="retryFailed">
            {{ adminText('organizationAll.retry_confirmed_failures_ff4e1d70', 'RETRY CONFIRMED FAILURES') }}
          </main-button>
        </div>
        <p v-if="retryError" role="alert" class="delivery-message">{{ organizationText(retryError) }}</p>
        <p v-if="retryResult" role="status" class="delivery-message">{{ organizationText(retryResult) }}</p>
      </template>
      <div class="actions"><main-button flat @click="goBack">{{ adminText('organizationAll.go_back_502bbc90', 'GO BACK') }}</main-button></div>
    </div>

    <div v-if="email.id">
      <div class="meta-panel">
        <div class="meta-row">
          <span class="meta-label">{{ adminText('organizationAll.reply_to_e_mail_db1a22d3', 'Reply to e-mail') }}</span>
          <span class="meta-value">{{ email.replyTo || '—' }}</span>
        </div>
        <div class="meta-row">
          <span class="meta-label">{{ adminText('organizationAll.created_accf40c8', 'Created') }}</span>
          <span class="meta-value">{{ createdAt }}</span>
        </div>
        <div class="meta-row">
          <span class="meta-label">{{ adminText('organizationAll.recipients_78cbf8eb', 'Recipients') }}</span>
          <span class="meta-value">{{ recipientSummary }}</span>
        </div>
        <div v-if="summary" class="meta-row">
          <span class="meta-label">{{ adminText('organizationAll.delivery_9631af52', 'Delivery') }}</span>
          <span class="meta-value">{{ organizationText(summary.status) }}</span>
          <span>{{ summary.sent }} {{ adminText('organizationAll.accepted_by_provider_838834ef', 'accepted by provider ·') }} {{ summary.failed }} {{ adminText('organizationAll.confirmed_failed_8593aa1c', 'confirmed failed ·') }} {{ summary.pending }} {{ adminText('organizationAll.pending_or_sending_4aa5f8b2', 'pending or sending ·') }} {{ summary.unknown }} {{ adminText('organizationAll.unknown_50d8b4a9', 'unknown') }}</span>
          <span v-if="summary.unknown">{{ adminText('organizationAll.unknown_outcomes_need_support_review_and_cann_045125da', 'Unknown outcomes need support review and cannot be retried here.') }}</span>
          <span>{{ adminText('organizationAll.provider_acceptance_does_not_confirm_inbox_de_cf1e8c06', 'Provider acceptance does not confirm inbox delivery.') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import moment from 'moment'
import gql from 'graphql-tag'
import { theaterOrganizationRouteNames } from '@/routing/routeNames/theaterOrganizerRouterNames'

const GET_THEATER_SUBSCRIPTION_EMAIL = gql`
  query GetTheaterSubscriptionEmail($organizationSlug: String!, $emailId: ID!) {
    getTheaterSubscriptionEmail(organizationSlug: $organizationSlug, emailId: $emailId) {
      id subject content replyTo createdAt
      emailRecipient { recipientType identifiers }
      theaterDeliverySummary { status total sent failed pending unknown }
    }
  }
`

const RETRY_FAILED_THEATER_SUBSCRIPTION_EMAIL = gql`
  mutation RetryFailedTheaterSubscriptionEmail($organizationSlug: String!, $emailId: ID!) {
    retryFailedTheaterSubscriptionEmail(organizationSlug: $organizationSlug, emailId: $emailId)
  }
`

export default {
  mixins: [OrganizationLocalization],
  name: 'theater-subscriptions-email-details',
  props: {
    organizationSlug: {
      type: String,
      required: true,
    },
    emailId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      email: {},
      loadedEmailId: null,
      loadedOrganizationSlug: null,
      loading: false,
      loadError: false,
      retrying: false,
      retryError: '',
      retryResult: '',
      detailRequestId: 0,
      retryRequestId: 0,
    }
  },
  computed: {
    summary() {
      return this.email.theaterDeliverySummary
    },
    canRetry() {
      return this.summary && this.summary.failed > 0 &&
        this.loadedEmailId === this.emailId && this.loadedOrganizationSlug === this.organizationSlug
    },
    createdAt() {
      const locale = this.isAdminLocalization ? this.$adminLocale() : moment.locale()
      const dateFormat = this.isAdminLocalization && locale === 'hr' ? 'D[.] MMMM YYYY HH:mm' : 'D. MMMM YYYY HH:mm'

      return this.email.createdAt ? moment(this.email.createdAt).locale(locale).format(dateFormat) : '—'
    },
    recipientSummary() {
      const recipient = this.email.emailRecipient
      if (!recipient) {
        return '—'
      }
      const count = (recipient.identifiers || []).length

      return count ? `${this.organizationRecipientLabel(recipient.recipientType)} (${count})` : this.organizationRecipientLabel(recipient.recipientType) || '—'
    },
  },
  watch: {
    emailId() {
      this.resetRouteEmail()
    },
    organizationSlug() {
      this.resetRouteEmail()
    },
  },
  created() {
    this.fetchEmail()
  },
  methods: {
    resetRouteEmail() {
      this.detailRequestId++
      this.retryRequestId++
      this.email = {}
      this.loadedEmailId = null
      this.loadedOrganizationSlug = null
      this.retrying = false
      this.retryError = ''
      this.retryResult = ''

      return this.fetchEmail()
    },
    async fetchEmail() {
      const requestId = ++this.detailRequestId
      const organizationSlug = this.organizationSlug
      const emailId = this.emailId
      this.loading = true
      this.loadError = false
      try {
        const { data } = await this.$apollo.query({
          query: GET_THEATER_SUBSCRIPTION_EMAIL,
          variables: { organizationSlug, emailId },
          fetchPolicy: 'no-cache',
        })
        if (requestId !== this.detailRequestId || organizationSlug !== this.organizationSlug || emailId !== this.emailId) return
        if (!data || !data.getTheaterSubscriptionEmail) throw new Error('Email details unavailable')
        this.email = data.getTheaterSubscriptionEmail
        this.loadedEmailId = emailId
        this.loadedOrganizationSlug = organizationSlug
      } catch (error) {
        if (requestId !== this.detailRequestId || organizationSlug !== this.organizationSlug || emailId !== this.emailId) return
        this.email = {}
        this.loadedEmailId = null
        this.loadedOrganizationSlug = null
        this.loadError = true
      } finally {
        if (requestId === this.detailRequestId && organizationSlug === this.organizationSlug && emailId === this.emailId) this.loading = false
      }
    },
    async retryFailed() {
      if (!this.canRetry || this.retrying) return
      const requestId = ++this.retryRequestId
      const organizationSlug = this.organizationSlug
      const emailId = this.emailId
      this.retrying = true
      this.retryError = ''
      this.retryResult = ''
      try {
        const result = await this.$apollo.mutate({
          mutation: RETRY_FAILED_THEATER_SUBSCRIPTION_EMAIL,
          variables: { organizationSlug, emailId },
        })
        if (requestId !== this.retryRequestId || organizationSlug !== this.organizationSlug || emailId !== this.emailId) return
        if (!result.data || result.data.retryFailedTheaterSubscriptionEmail !== true) throw new Error('Retry not accepted')
        await this.fetchEmail()
        if (requestId !== this.retryRequestId || organizationSlug !== this.organizationSlug || emailId !== this.emailId) return
        this.retryResult = 'Retry queued. Refresh delivery status to follow progress; unknown outcomes need support review.'
      } catch (error) {
        if (requestId !== this.retryRequestId || organizationSlug !== this.organizationSlug || emailId !== this.emailId) return
        await this.fetchEmail()
        if (requestId !== this.retryRequestId || organizationSlug !== this.organizationSlug || emailId !== this.emailId) return
        this.retryError = 'Retry could not be confirmed. Check the delivery status before trying again.'
      } finally {
        if (requestId === this.retryRequestId && organizationSlug === this.organizationSlug && emailId === this.emailId) this.retrying = false
      }
    },
    goBack() {
      this.$router.push({
        name: theaterOrganizationRouteNames.theaterOrganizationTheaterSubscribersEmails,
        params: { organizationSlug: this.organizationSlug },
      })
    },
  },
}
</script>

<style scoped>
.email-details {
  display: grid;
  grid-template-columns: minmax(0, 684fr) minmax(0, 330fr);
  gap: 24px;
  align-items: start;
}
.email-body {
  min-width: 0;
}
.title-screen {
  margin: 0 0 32px;
  font-family: 'Raleway', sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.03em;
  color: #ff4c00;
}
.email-subject {
  color: #0277bd;
  font-family: 'Raleway', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.03em;
  margin: 0 0 32px;
  overflow-wrap: anywhere;
}
.email-content {
  color: #333;
  font-family: 'Roboto', sans-serif;
  font-size: 16px;
  line-height: 1.5;
  letter-spacing: 0.5px;
  overflow-wrap: anywhere;
  overflow-x: auto;
}
.actions {
  margin-top: 48px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.delivery-message { margin: 16px 0; color: #333; }
.actions .main-button {
  margin: 0;
  height: 48px;
  padding: 12px 24px;
}
.meta-panel {
  background: #fceae6;
  border-radius: 8px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.meta-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.meta-label {
  color: #979797;
  font-size: 14px;
  line-height: 1.2;
}
.meta-value {
  color: #333;
  font-size: 18px;
  line-height: 1.5;
  overflow-wrap: anywhere;
}
@media (max-width: 959px) {
  .email-details {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
